/**
 * Editor da prescrição de força (A3): escolhe exercícios ativos da biblioteca da assessoria e monta as séries.
 * A validação final (exercício da assessoria, parâmetros obrigatórios) é do servidor.
 */
import { useEffect, useState, type ReactNode } from "react";
import { api } from "../api/client";
import type { Exercise, Page } from "../api/types";
import { useAsync } from "../lib/useAsync";
import { PARAM_LABEL } from "../lib/strength";
import { newItem, newSet, type EditItem, type EditSet, type EditStrength, type SetErrors } from "../lib/strengthEdit";
import { Button, TextArea } from "./ui";
import { t } from "../i18n";

const ERR: Record<string, () => string> = {
  reps: () => t("Informe as repetições (1 a 1000)."),
  reps_range: () => t("Informe a faixa de repetições (mínimo até máximo)."),
  load: () => t("Carga inválida (0 a 1000 kg)."),
  duration: () => t("Duração inválida. Use mm:ss."),
  distance: () => t("Distância inválida (metros, 1 a 100000)."),
  rest: () => t("Descanso inválido (segundos, até 3600)."),
  tempo: () => t("Cadência inválida. Use o formato 3-1-1-0."),
};

function move<T>(list: T[], i: number, d: number): T[] {
  const j = i + d;
  if (j < 0 || j >= list.length) return list;
  const copy = [...list];
  [copy[i], copy[j]] = [copy[j], copy[i]];
  return copy;
}

function Field({ id, label, error, children }: { id: string; label: string; error?: string; children: ReactNode }) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children}
      {error && <span className="field-error" role="alert">{ERR[error]?.() ?? error}</span>}
    </div>
  );
}

function SetRow({
  item,
  set,
  no,
  errors,
  onChange,
  onRemove,
  canRemove,
}: {
  item: EditItem;
  set: EditSet;
  no: number;
  errors?: SetErrors;
  onChange: (s: EditSet) => void;
  onRemove: () => void;
  canRemove: boolean;
}) {
  const p = item.paramType;
  const id = (n: string) => `set-${set.id}-${n}`;
  const upd = (patch: Partial<EditSet>) => onChange({ ...set, ...patch });
  const input = (n: keyof EditSet & string, label: string, extra: Record<string, string> = {}) => (
    <Field id={id(n)} label={label} error={errors?.[n as keyof SetErrors]}>
      <input id={id(n)} className="input" value={String(set[n])} onChange={(e) => upd({ [n]: e.target.value } as Partial<EditSet>)}
        aria-invalid={errors?.[n as keyof SetErrors] ? true : undefined} {...extra} />
    </Field>
  );
  return (
    <div className="set-row" role="group" aria-label={t("Série {n} de {name}", { n: no, name: item.name })}>
      <span className="set-no">{no}</span>
      {(p === "reps_load" || p === "reps") && (
        <>
          {set.range ? (
            <>
              {input("repsMin", t("Rep. mín."), { inputMode: "numeric" })}
              {input("repsMax", t("Rep. máx."), { inputMode: "numeric" })}
            </>
          ) : (
            input("reps", t("Repetições"), { inputMode: "numeric" })
          )}
        </>
      )}
      {p === "reps_load" && input("load", t("Carga (kg)"), { inputMode: "decimal" })}
      {p === "time" && input("duration", t("Duração"), { placeholder: "mm:ss" })}
      {p === "distance" && input("distance", t("Distância (m)"), { inputMode: "numeric" })}
      {input("rest", t("Descanso (s)"), { inputMode: "numeric" })}
      <div className="set-extra">
        {(p === "reps_load" || p === "reps") && (
          <label className="checkbox">
            <input type="checkbox" checked={set.range} onChange={(e) => upd({ range: e.target.checked })} />
            {t("Faixa de repetições")}
          </label>
        )}
        <Button size="sm" variant="ghost" onClick={onRemove} disabled={!canRemove} aria-label={t("Remover série {n}", { n: no })}>
          {t("Remover")}
        </Button>
      </div>
    </div>
  );
}

function ExercisePicker({ orgId, onPick }: { orgId: string; onPick: (e: Exercise) => void }) {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const tp = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(tp);
  }, [q]);
  const list = useAsync(
    () => api.get<Page<Exercise>>(`/orgs/${orgId}/exercises`, { q: debounced || undefined, page: 1, page_size: 8 }),
    [orgId, debounced],
  );
  return (
    <div className="ex-picker">
      <div className="field">
        <label htmlFor="ex-picker-q">{t("Adicionar exercício")}</label>
        <input id="ex-picker-q" className="input" type="search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder={t("Buscar na biblioteca")} />
      </div>
      {list.data && list.data.items.length === 0 && (
        <p className="muted">
          {debounced ? t("Nenhum exercício encontrado.") : t("A biblioteca está vazia. Cadastre exercícios em Exercícios.")}
        </p>
      )}
      <ul className="ex-picker-list">
        {list.data?.items.map((e) => (
          <li key={e.id}>
            <button type="button" className="ex-pick" onClick={() => onPick(e)}>
              <strong>{e.name}</strong>
              <span className="muted">{PARAM_LABEL[e.param_type]}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function StrengthEditor({
  orgId,
  value,
  onChange,
  errors,
}: {
  orgId: string;
  value: EditStrength;
  onChange: (v: EditStrength) => void;
  errors: Record<number, SetErrors>;
}) {
  const setItem = (i: number, it: EditItem) => onChange({ ...value, items: value.items.map((x, j) => (j === i ? it : x)) });
  return (
    <div className="strength-editor">
      {value.items.length === 0 && <p className="muted">{t("Escolha os exercícios da biblioteca e defina as séries de cada um.")}</p>}
      {value.items.map((it, i) => (
        <div key={it.id} className="se-item">
          <div className="se-head">
            <div>
              <strong>{it.name}</strong>
              <span className="muted"> · {PARAM_LABEL[it.paramType]}</span>
            </div>
            <div className="step-actions">
              <Button size="sm" variant="ghost" disabled={i === 0} aria-label={t("Mover exercício para cima")}
                onClick={() => onChange({ ...value, items: move(value.items, i, -1) })}>↑</Button>
              <Button size="sm" variant="ghost" disabled={i === value.items.length - 1} aria-label={t("Mover exercício para baixo")}
                onClick={() => onChange({ ...value, items: move(value.items, i, 1) })}>↓</Button>
              <Button size="sm" variant="ghost" onClick={() => onChange({ ...value, items: value.items.filter((_, j) => j !== i) })}>
                {t("Remover")}
              </Button>
            </div>
          </div>
          {it.sets.map((s, k) => (
            <SetRow
              key={s.id}
              item={it}
              set={s}
              no={k + 1}
              errors={errors[s.id]}
              canRemove={it.sets.length > 1}
              onChange={(ns) => setItem(i, { ...it, sets: it.sets.map((x) => (x.id === s.id ? ns : x)) })}
              onRemove={() => setItem(i, { ...it, sets: it.sets.filter((x) => x.id !== s.id) })}
            />
          ))}
          <div className="se-foot">
            <Button size="sm" onClick={() => setItem(i, { ...it, sets: [...it.sets, newSet(it.sets[it.sets.length - 1])] })}
              disabled={it.sets.length >= 30}>
              {t("Adicionar série")}
            </Button>
            <input className="input" aria-label={t("Observação do exercício")} placeholder={t("Observação do exercício (opcional)")}
              value={it.note} maxLength={300} onChange={(e) => setItem(i, { ...it, note: e.target.value })} />
          </div>
        </div>
      ))}
      {value.items.length < 30 && (
        <ExercisePicker orgId={orgId} onPick={(e) => onChange({ ...value, items: [...value.items, newItem(e)] })} />
      )}
      <TextArea label={t("Observações gerais da sessão de força")} value={value.notes} maxLength={1000}
        onChange={(e) => onChange({ ...value, notes: e.target.value })} />
    </div>
  );
}
